import { useAskInputModeToggleGroupProps } from '#pplx/frontend/apps/ask/ask-input/ask-input-attributions/hooks/useAskInputModeToggleGroupProps.js';
import { useAskInputModeToggleGroupRenderMenu } from '#pplx/frontend/apps/ask/ask-input/ask-input-attributions/hooks/useAskInputModeToggleGroupRenderMenu.js';
import { useAskInputSearchModes } from '#pplx/frontend/apps/ask/ask-input/ask-input-attributions/hooks/useAskInputSearchModes.js';
import { getAskModeToggleLabelVisibility } from '#pplx/frontend/apps/ask/composer-sdk-plugins/mode-toggle/getAskModeToggleLabelVisibility.js';
import { useSelectedSearchMode } from '#pplx/frontend/apps/ask/hooks/selected-search-mode-hooks/useSelectedSearchMode.js';
import { useThreadConfig } from '#pplx/frontend/apps/ask/providers/ThreadSettingsProviderShared.js';
import { SearchMode } from '#pplx/frontend/ask-models/types/search-modes.js';
import { isFramedAppearance } from '#pplx/frontend/composer-sdk/styles.js';
import { useIsMobileStyle } from '#pplx/frontend/platform-components/useMobileStyle.js';
import { useCallback, useMemo } from 'react';
const NOOP_SELECT = (_mode) => { };
/** @returns The visible controls, or null when neither a toggle nor Browser status is available. */
export function useSearchModeToggleSlotProps({ appearance, enabled = true, isFollowUp, canClearSearchMode, omittedSearchModes, searchMode, selectSearchModeOverride, onSearchModeSelected, onSearchModeCleared, toggleVisibility, renderToggle, computerCommandMenu, onAfterSelect, }) {
    const { hideSearchModeToggle } = useThreadConfig();
    const selectedSearchMode = useSelectedSearchMode();
    const isMobileStyle = useIsMobileStyle();
    // Accepted selections fire both the surface's callback and the composer's refocus.
    const handleSearchModeSelected = useCallback((mode) => {
        onSearchModeSelected?.(mode);
        onAfterSelect?.();
    }, [onSearchModeSelected, onAfterSelect]);
    const { searchModeControllerProps, searchModeMenuProps } = useAskInputSearchModes({
        enabled,
        isFollowUp,
        searchMode,
        ...(selectSearchModeOverride && { onSearchModeSelect: selectSearchModeOverride }),
        omittedSearchModes,
        onSearchModeSelected: handleSearchModeSelected,
        onSearchModeCleared,
    });
    const renderMenu = useAskInputModeToggleGroupRenderMenu({
        searchModeControllerProps,
        // Composer owns the Computer overlay; Search still uses its existing renderer.
        showComputerSkillsDropdown: false,
    });
    const controllerSearchMode = searchModeControllerProps.showSearchMode
        ? searchModeControllerProps.searchMode
        : selectedSearchMode;
    const controllerOnSearchModeSelect = searchModeControllerProps.showSearchMode
        ? searchModeControllerProps.onSearchModeSelect
        : NOOP_SELECT;
    const controllerOmittedSearchModes = searchModeControllerProps.showSearchMode
        ? searchModeControllerProps.omittedSearchModes
        : omittedSearchModes;
    const isSearchModeDisabled = searchModeControllerProps.showSearchMode
        ? searchModeControllerProps.isSearchModeDisabled
        : undefined;
    const modeToggleGroupProps = useAskInputModeToggleGroupProps({
        searchMode: controllerSearchMode,
        selectedSearchMode,
        onSearchModeSelect: controllerOnSearchModeSelect,
        isFollowUp,
        omittedSearchModes: controllerOmittedSearchModes,
        isSearchModeDisabled,
        toggleVisibility,
        renderComputerModeToggle: renderToggle,
        renderMenu,
        autoApplyEnabled: searchModeControllerProps.showSearchMode,
        labelVisibility: getAskModeToggleLabelVisibility(isMobileStyle),
        appearance: isFramedAppearance(appearance) ? appearance : 'default',
    });
    const controlledModeToggleGroupProps = useMemo(() => computerCommandMenu === undefined
        ? modeToggleGroupProps
        : {
            ...modeToggleGroupProps,
            items: modeToggleGroupProps.items.map((item) => item.value === SearchMode.ASI ? { ...item, ...computerCommandMenu } : item),
        }, [computerCommandMenu, modeToggleGroupProps]);
    if (enabled && hideSearchModeToggle && computerCommandMenu !== undefined) {
        return { kind: 'command-button', computerCommandMenu };
    }
    if (searchModeControllerProps.showSearchMode) {
        return {
            kind: 'toggle-group',
            modeToggleGroupProps: controlledModeToggleGroupProps,
            showSearchModeMenu: searchModeMenuProps.showSearchModeMenu ?? false,
        };
    }
    if (searchModeMenuProps.showSearchModeMenu &&
        searchModeMenuProps.searchMode === SearchMode.BROWSER_AGENT) {
        return {
            kind: 'browser-status',
            searchModeMenuProps: canClearSearchMode === undefined
                ? searchModeMenuProps
                : {
                    ...searchModeMenuProps,
                    clearable: canClearSearchMode,
                    disabled: !canClearSearchMode,
                    disabledTooltip: canClearSearchMode ? undefined : searchModeMenuProps.disabledTooltip,
                },
        };
    }
    return null;
}
//# sourceMappingURL=useSearchModeToggleSlotProps.js.map